import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  CardContent, 
  Typography, 
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import { 
  Gavel as AppealsIcon,
  Add as AddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { PageContainer, PageCard } from '../../common/components/styled-ui-elements';
import { AppealsGrid } from '../components';
import { useListAppealsByEncounterQuery, AppealStatus } from '../../../services/AppealsService';

const STATUS_COLORS: Record<AppealStatus, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'> = {
  Draft: 'default',
  Generated: 'info',
  Reviewed: 'primary',
  Approved: 'success',
  Sent: 'secondary',
  Pending: 'warning',
  Won: 'success',
  Lost: 'error',
  Withdrawn: 'default',
};

// This page lists one encounter's appeal letters: the only list endpoint is ListAppealsByEncounter,
// so without an encounter id there is nothing to read. Spec 212 made the Appeals menu's single entry
// point here, so arriving with no encounter is now the ordinary way in, not a mistake — it reads as
// the house empty state, not as a red error. Held at module scope so it costs the component body no
// lines: AppealsWorklist is already above the complexity threshold and the gate reports any growth.
const NO_ENCOUNTER_STATE = (
  <PageContainer>
    <Box display="flex" justifyContent="center">
      <PageCard sx={{ maxWidth: 800 }}>
        <CardContent sx={{ p: 3 }}>
          <Alert severity="info" data-testid="appeals-worklist-no-encounter">
            Appeal letters are listed per encounter. Open an encounter, then choose Appeals to
            see and create its appeal letters.
          </Alert>
        </CardContent>
      </PageCard>
    </Box>
  </PageContainer>
);

const AppealsWorklist: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const encounterId = searchParams.get('encounterId') || '';
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const status = searchParams.get('status') as AppealStatus | undefined;

  const [refreshKey, setRefreshKey] = useState(0);

  const { data: appeals, isLoading, error, refetch } = useListAppealsByEncounterQuery(
    { encounterId, status },
    { skip: !encounterId }
  );

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
    refetch();
  };

  const handleCreateNew = () => {
    navigate(`/appeals/create?encounterId=${encounterId}&company=${company}&center=${center}`);
  };

  if (!encounterId)
    return NO_ENCOUNTER_STATE;

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box sx={{ 
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #3b82f6',
            p: 2.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 20
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AppealsIcon sx={{ fontSize: 32, color: '#3b82f6' }} />
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#1e293b',
                    mb: 0.5
                  }}
                >
                  Claim Appeals
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ color: '#64748b' }}
                >
                  {company} | {center} | Encounter: {encounterId}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                sx={{ 
                  borderColor: '#3b82f6',
                  color: '#3b82f6',
                  '&:hover': {
                    borderColor: '#2563eb',
                    backgroundColor: '#eff6ff'
                  },
                  fontWeight: 600
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateNew}
                sx={{ 
                  backgroundColor: '#3b82f6',
                  '&:hover': {
                    backgroundColor: '#2563eb'
                  },
                  fontWeight: 600
                }}
              >
                New Appeal
              </Button>
            </Box>
          </Box>
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {/* Status Filter Chips */}
            <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ mr: 1, alignSelf: 'center', color: '#64748b' }}>
                Filter by status:
              </Typography>
              {Object.keys(STATUS_COLORS).map((statusKey) => (
                <Chip
                  key={statusKey}
                  label={statusKey}
                  color={status === statusKey ? STATUS_COLORS[statusKey as AppealStatus] : 'default'}
                  variant={status === statusKey ? 'filled' : 'outlined'}
                  onClick={() => {
                    const newParams = new URLSearchParams(searchParams);
                    if (status === statusKey) {
                      newParams.delete('status');
                    } else {
                      newParams.set('status', statusKey);
                    }
                    navigate(`/appeals?${newParams.toString()}`);
                  }}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>

            {/* Loading State */}
            {isLoading && (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            )}

            {/* Error State */}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load appeals. Please try again.
              </Alert>
            )}

            {/* Appeals Grid */}
            {!isLoading && !error && (
              <AppealsGrid key={refreshKey} appeals={appeals || []} />
            )}
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

export default AppealsWorklist;
